// CurveSetSelector — select a curve set, then pick a curve by role for pricing
// Replaces the old CurveSelector in bond/swap pricers
import { useState, useEffect } from 'react';
import { CurveSet, Curve, CurveRole } from '../../lib/types';
import { getSavedCurveSets, resolveCurveSetRefs } from '../../lib/storage/curveSets';
import { getSavedCurves } from '../../lib/storage/curves';

interface CurveSetSelectorProps {
  label: string;
  curveRole: CurveRole;           // which role to filter (discount / forward)
  curveSetId: string;
  curveId: string;
  onChangeCurveSet: (csId: string, cs?: CurveSet | null) => void;
  onChangeCurve: (curveId: string, curve: Curve | null) => void;
  className?: string;
}

export default function CurveSetSelector({
  label, curveRole, curveSetId, curveId,
  onChangeCurveSet, onChangeCurve, className,
}: CurveSetSelectorProps) {
  const [curveSets, setCurveSets] = useState<CurveSet[]>([]);
  const [standaloneCurves, setStandaloneCurves] = useState<Curve[]>([]);
  const [selectedCs, setSelectedCs] = useState<CurveSet | null>(null);

  useEffect(() => {
    const sets = getSavedCurveSets();
    setCurveSets(sets);
    setStandaloneCurves(getSavedCurves());

    // Auto-resolve on mount
    if (curveSetId) {
      const cs = sets.find(s => s.id === curveSetId);
      if (cs) {
        setSelectedCs(cs);
        onChangeCurveSet(curveSetId, cs);
        if (curveId) {
          const c = resolveCurveSetRefs(cs).find(ref => ref.curve?.id === curveId)?.curve || null;
          if (c) onChangeCurve(curveId, c);
        }
      }
    } else if (curveId) {
      // Legacy: standalone curve selected
      const c = getSavedCurves().find(c => c.id === curveId);
      if (c) onChangeCurve(curveId, c);
    }
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const handleCurveSetChange = (csId: string) => {
    if (csId === '__standalone__') {
      setSelectedCs(null);
      onChangeCurveSet('', null);
      onChangeCurve('', null);
      return;
    }
    const cs = curveSets.find(s => s.id === csId) || null;
    setSelectedCs(cs);
    onChangeCurveSet(csId, cs);
    // Auto-select first matching curve
    if (cs) {
      const matching = resolveCurveSetRefs(cs)
        .filter(ref => ref.role === curveRole)
        .map(ref => ref.curve)
        .filter((curve): curve is Curve => curve !== null);
      if (matching.length === 1) {
        onChangeCurve(matching[0].id, matching[0]);
      } else {
        onChangeCurve('', null);
      }
    } else {
      onChangeCurve('', null);
    }
  };

  const handleCurveChange = (cId: string) => {
    if (selectedCs) {
      const c = resolveCurveSetRefs(selectedCs).find(ref => ref.curve?.id === cId)?.curve || null;
      onChangeCurve(cId, c);
    } else {
      // Standalone
      const c = standaloneCurves.find(c => c.id === cId) || null;
      onChangeCurve(cId, c);
    }
  };

  const availableCurves = selectedCs
    ? resolveCurveSetRefs(selectedCs)
        .filter(ref => ref.role === curveRole)
        .map(ref => ref.curve)
        .filter((curve): curve is Curve => curve !== null)
    : standaloneCurves.filter(c => c.role === curveRole);

  return (
    <div className={className}>
      <label className="block text-xs text-[#737373] mb-1.5 font-medium">{label}</label>
      <div className="space-y-2">
        {/* Curve Set selector */}
        <select
          value={curveSetId || (selectedCs ? '' : '__standalone__')}
          onChange={e => handleCurveSetChange(e.target.value)}
          className="w-full px-3 py-2 bg-white border border-[#d4d4d4] rounded-lg text-sm text-[#0a0a0a] focus:outline-none focus:border-[#8a6a2f] transition-colors"
        >
          <option value="">Select a curve set...</option>
          {curveSets.map(cs => (
            <option key={cs.id} value={cs.id}>
              {cs.name} ({cs.currency}) — {(cs.curve_refs || []).length} references
            </option>
          ))}
          {standaloneCurves.length > 0 && (
            <option value="__standalone__">— Standalone curves —</option>
          )}
        </select>

        {/* Curve selector within set */}
        {(selectedCs || curveSetId === '') && (
          <select
            value={curveId}
            onChange={e => handleCurveChange(e.target.value)}
            className="w-full px-3 py-2 bg-white border border-[#d4d4d4] rounded-lg text-sm text-[#0a0a0a] focus:outline-none focus:border-[#8a6a2f] transition-colors"
          >
            <option value="">
              {selectedCs
                ? `Select ${curveRole} curve...`
                : 'Select a curve...'
              }
            </option>
            {availableCurves.map(c => (
              <option key={c.id} value={c.id}>
                {c.name} ({c.role})
              </option>
            ))}
          </select>
        )}
      </div>
      {curveSets.length === 0 && standaloneCurves.length === 0 && (
        <p className="text-xs text-[#a3a3a3] mt-1">
          No curves available. <a href="/yield-curves" className="text-[#8a6a2f] hover:underline">Create a standalone curve</a>
        </p>
      )}
    </div>
  );
}
